'use client';

import FreestarAdSlot from '@freestar/pubfig-adslot-react-component';
import { useAdContext, useAdContextConfig } from '@/app_lib/providers/AdProvider';
import { leaderboardManager } from '@/app_lib/services/ads/leaderboardManager';
import type { AdSlotInfo, PlacementName } from '@/app_lib/types/Ad';
import { checkForDirectSoldAd, freeStarPlacementNameFinder, publisher } from '@/app_lib/utils/ads';

export interface AdSlotProps {
  placementName: PlacementName;
  index?: number;
  sticky?: boolean;
}

/**
 * Creates a slot to render an ad based on the placement, index, and sticky properties.
 *
 * @param placementName - The name of the ad placement to render.
 * @param index - The index of the ad slot.
 * @param sticky - Whether the ad slot should be sticky.
 * @returns A React element rendering the configured ad slot.
 *
 * IMPORTANT: The pageAdConfig should not be undefined when this component is rendered.
 * It is expected to be provided by the AdProvider context.
 * If pageAdConfig is undefined, this component will not function correctly due to react hooks.
 */
export const AdSlot = ({ placementName, index = 0, sticky = false }: AdSlotProps) => {
  const { pageAdConfig } = useAdContext();
  // biome-ignore lint/style/noNonNullAssertion: <We know that pageAdConfig is defined by now, so we can safely use it>
  const adConfig = pageAdConfig!;
  const { getPlacementConfig } = useAdContextConfig(adConfig);
  const placementConfig = getPlacementConfig(placementName);
  const finalPath = freeStarPlacementNameFinder(placementName, sticky, index, placementConfig.path);
  const classToUse = placementName === 'Leaderboard' ? 'leaderboard-ad-wrapper' : 'ad-slot-wrapper';

  const freeStarSlot = {
    channel: placementConfig.path,
    classList: [classToUse],
    placementName: finalPath,
    publisher,
    // biome-ignore lint/style/useNamingConvention: <external data>
    targeting: { index, placement_name: placementName, sticky },
  } as AdSlotInfo;

  if (placementName === 'Leaderboard') {
    freeStarSlot.slotId = 'Leaderboard0';
    freeStarSlot.onNewAdSlotsHook = (e) => {
      if (e === 'fortunecom_leaderboard_top') {
        leaderboardManager();
      }
    };
  }

  const directSoldAd = checkForDirectSoldAd(adConfig.tags, adConfig.ctype, placementName, placementConfig);
  if (directSoldAd.sizeMappings) {
    freeStarSlot.adUnitPath = placementConfig.path;
    freeStarSlot.slotSize = directSoldAd.slotSize;
    freeStarSlot.sizeMappings = directSoldAd.sizeMappings;
  }
  return <FreestarAdSlot {...freeStarSlot} />;
};
